import React from 'react';
import { useStaticQuery, graphql, navigate } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  boxShadow,
  brandColours,
  breakpointSizes,
  fluidFontSize,
  fontSize,
  fontWeights,
  minBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Button, Svg } from './ui';
import speechBubble from '../images/speech-bubble.inline.svg';
import simCardIcon from '../images/sim-card-icon.png';
import { buildUrl, dataLayer, formatPrice } from '../utils';

const StyledPlanCard = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${standardColours.white};
  ${boxShadow()};
`;

const StyledSpeechBubble = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: 110px;
  transform: translate(-16%, -40%);

  ${minBreakpointQuery.small`
    width: 130px;
    transform: translate(-18%, -45%);
  `}

  ${minBreakpointQuery.xxlarge`
    width: 150px;
    transform: translate(-20%, -50%);
  `}

  svg {
    display: block;
    height: 100%;
    width: 100%;
  }

  span {
    position: absolute;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px;
    width: 100%;
    font-weight: ${fontWeights.bold};
    line-height: 1.1;
    text-align: center;

    ${minBreakpointQuery.small`
      padding: 12px;
      ${fontSize(18)};
    `}

    ${minBreakpointQuery.large`
      padding: 14px;
      ${fontSize(20)};
    `}
  }

  ${({ $alt }) => {
    if ($alt) {
      return css`
        svg {
          fill: ${brandColours.secondary};
          transform: rotate(180deg);
        }

        span {
          top: 20%;
          bottom: 0;
        }
      `;
    } else {
      return css`
        svg {
          fill: ${brandColours.primary};
        }

        span {
          top: 0;
          bottom: 20%;
        }
      `;
    }
  }}
`;

const StyledInner = styled.div`
  ${({ $isPlanDetailPage }) => {
    if (!$isPlanDetailPage) {
      return css`
        padding: 20px 30px;

        ${minBreakpointQuery.small`
          padding: 25px 35px;
        `}

        @media (min-width: ${breakpointSizes.smedium}px) and (max-width: ${breakpointSizes.large -
        1}px) {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 30px;
        }

        ${minBreakpointQuery.xxlarge`
          padding-right: 40px;
          padding-left: 40px;
        `}

        ${minBreakpointQuery.xxxlarge`
          padding: 30px 45px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          padding-right: 50px;
          padding-left: 50px;
        `}
      `;
    } else {
      return css`
        padding: 20px 20px;

        ${minBreakpointQuery.small`
          padding: 25px 30px;
        `}
      `;
    }
  }}
`;

const StyledHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: -20px;
`;

const StyledSimCardIcon = styled.img`
  width: 34%;
  max-width: 145px;
`;

const StyledOfferName = styled.div``;

const StyledOffer = styled.p`
  margin-bottom: 12px;
  max-width: 150px;
  font-weight: ${fontWeights.semibold};
  line-height: 1;
  border-left: 5px solid ${brandColours.tertiary};

  ${minBreakpointQuery.small`
    margin-bottom: 14px;
    max-width: 165px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.xxlarge`
    margin-bottom: 16px;
    max-width: 180px;
    ${fontSize(20)};
  `}

  > span {
    position: relative;
    display: inline-block;
    background-color: ${brandColours.tertiary};

    &:before,
    &:after {
      content: '';
      position: absolute;
      left: -5px;
      height: 5px;
      width: calc(100% + 5px);
      background-color: ${brandColours.tertiary};
    }

    &:before {
      top: -5px;
    }

    &:after {
      bottom: -5px;
    }

    > span {
      position: relative;
      z-index: 1;
    }
  }
`;

const StyledData = styled.h3`
  ${fluidFontSize(
    '30px',
    '45px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge,
  )};
  line-height: 1;

  s {
    position: relative;
    display: inline-block;
    text-decoration: none;

    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: -10%;
      height: 2px;
      width: 120%;
      background-color: ${brandColours.primary};
      transform: translateY(-50%) rotate(-20deg);

      ${minBreakpointQuery.small`
        height: 3px;
      `}

      ${minBreakpointQuery.xxlarge`
        height: 4px;
      `}
    }
  }
`;

const StyledContent = styled.div`
  margin-top: 15px;

  ${({ $isPlanDetailPage }) => {
    if ($isPlanDetailPage) {
      return css`
        --content-left-right-padding: 20px;

        ${minBreakpointQuery.xxlarge`
          --content-left-right-padding: 25px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          --content-left-right-padding: 30px;
        `}
      `;
    } else {
      return css`
        --content-left-right-padding: 30px;

        ${minBreakpointQuery.xxlarge`
          --content-left-right-padding: 35px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          --content-left-right-padding: 40px;
        `}
      `;
    }
  }}
`;

const StyledList = styled.ul`
  padding: 0 var(--content-left-right-padding);
  list-style-type: disc;
`;

const StyledListItem = styled.li`
  margin: 6px 0 0 14px;
  line-height: 1.15;

  ${({ $isPlanDetailPage }) => {
    if ($isPlanDetailPage) {
      return css`
        ${fontSize(14)};

        ${minBreakpointQuery.tiny`
          ${fontSize(16)};
        `}

        ${minBreakpointQuery.small`
          margin-left: 16px;
          ${fontSize(18)};
        `}

        ${minBreakpointQuery.medium`
          ${fontSize(16)};
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.small`
          margin-left: 16px;
          ${fontSize(18)};
        `}

        ${minBreakpointQuery.xxlarge`
          margin-left: 18px;
          ${fontSize(20)};
        `}
      `;
    }
  }}

  &:first-child {
    margin-top: 0;
  }
`;

const StyledPrice = styled.p`
  margin: 20px 0;
  padding: 10px var(--content-left-right-padding);
  ${fontSize(20)};
  border-top: 1px solid ${brandColours.quaternary};
  border-bottom: 1px solid ${brandColours.quaternary};

  ${minBreakpointQuery.small`
    margin-top: 24px;
    margin-bottom: 24px;
    padding-top: 12px;
    padding-bottom: 12px;
    ${fontSize(22)};
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 28px;
    margin-bottom: 28px;
    padding-top: 14px;
    padding-bottom: 14px;
    ${fontSize(24)};
  `}

  span {
    display: inline-block;
    margin-right: 4px;
    font-weight: ${fontWeights.bold};
    ${fontSize(36)};

    ${minBreakpointQuery.small`
      ${fontSize(38)};
    `}

    ${minBreakpointQuery.xxlarge`
      ${fontSize(40)};
    `}
  }
`;

const StyledButton = styled(Button)`
  margin-top: auto;
  width: 100%;
  color: ${brandColours.quaternary};
  background-color: ${standardColours.white};
  border-top: 1px solid ${brandColours.octonary};
  border-radius: 0;
  transition: ${standardTransition('color')},
    ${standardTransition('background-color')};

  &:hover {
    color: ${standardColours.white};
  }
`;

const PlanCard = plan => {
  const {
    name,
    offer,
    ecotalkId,
    previousDataGb,
    dataGb,
    pricePerMonth,
    highlight,
    isPlanDetailPage,
    ...props
  } = plan;

  const {
    contentfulSitewide: { features, usps },
  } = useStaticQuery(graphql`
    query PlanCardQuery {
      contentfulSitewide {
        features: plansFeatures
        usps: plansUsps
      }
    }
  `);

  return (
    <StyledPlanCard {...props}>
      {highlight && (
        <StyledSpeechBubble $alt={highlight[0] === 'Deal of the Day'}>
          <Svg image={speechBubble} />
          <span>{highlight}</span>
        </StyledSpeechBubble>
      )}
      <StyledInner $isPlanDetailPage={isPlanDetailPage}>
        <StyledHeader>
          <StyledSimCardIcon
            src={simCardIcon}
            alt="Sim card icon"
            loading="lazy"
          />
          <StyledOfferName>
            {offer && (
              <StyledOffer>
                {offer.split(' ').map((word, i) => (
                  <span key={i}>
                    <span>{word}</span>
                    &nbsp;
                  </span>
                ))}
              </StyledOffer>
            )}
            <StyledData>
              Unlimited {previousDataGb && <s>{previousDataGb}GB</s>} {dataGb}GB
            </StyledData>
          </StyledOfferName>
        </StyledHeader>
        <StyledContent $isPlanDetailPage={isPlanDetailPage}>
          <StyledList>
            {[...features, `${dataGb}GB month`, '1 Month'].map(
              (feature, id) => (
                <StyledListItem key={id} $isPlanDetailPage={isPlanDetailPage}>
                  {feature}
                </StyledListItem>
              ),
            )}
          </StyledList>
          <StyledPrice>
            <span>{formatPrice(pricePerMonth)}</span>
            /month
          </StyledPrice>
          <StyledList>
            {usps.map((usp, id) => (
              <StyledListItem key={id} $isPlanDetailPage={isPlanDetailPage}>
                {usp}
              </StyledListItem>
            ))}
          </StyledList>
        </StyledContent>
      </StyledInner>
      {!isPlanDetailPage && (
        <StyledButton
          isLarge={true}
          onClick={() => {
            dataLayer('add_to_cart', {
              ecommerce: {
                currency: 'GBP',
                value: pricePerMonth,
                items: [
                  {
                    item_id: ecotalkId,
                    item_name: name,
                    price: pricePerMonth,
                    quantity: 1,
                  },
                ],
              },
            });
            navigate(buildUrl(plan.slug, plan));
          }}
        >
          Select Plan
        </StyledButton>
      )}
    </StyledPlanCard>
  );
};

export default PlanCard;

export const PlanCardFragment = graphql`
  fragment PlanCardFragment on ContentfulPlan {
    name
    offer
    ecotalkId
    previousDataGb
    dataGb
    pricePerMonth
    highlight
    ...LinkFragment
  }
`;
